import { AuthUserType } from '../api'

/**
 * Who may see a nav entry.
 *
 * This replaces the wrapper components (`IsAuth` / `IsStaff`) the menu used to gate links with.
 * A wrapper decides visibility *during* render, which means a group cannot know how many of its
 * children survived — and a dropdown that opens onto nothing is worse than no dropdown at all.
 * A predicate can be evaluated up front, so grouping, counting and collapsing all become possible.
 */
export type NavAccess = 'all' | 'auth' | 'staff' | 'affiliate' | 'affiliate-manager' | 'packs' | 'marketplace'

export type NavItem = {
    label: string
    to: string
    access: NavAccess
    /** Opens in a new tab and skips router matching. */
    external?: boolean
    /** One short line shown under the label inside a dropdown panel. */
    description?: string
}

export type NavGroup = {
    label: string
    items: NavItem[]
}

/**
 * Grouped so each heading answers "what do I get out of this".
 * EARN is the three surfaces that pay out; SHOP is the two that spend.
 *
 * **Order matters.** Clicking a group's label in the menu bar navigates to its first internal item
 * — Play to Download, Earn to Tournaments, Shop to Store — so reordering a group also moves where
 * its parent click lands. Keep the most useful destination first.
 */
export const navGroups: NavGroup[] = [
    {
        label: 'Play',
        items: [
            {
                label: 'Download',
                to: '/download',
                access: 'all',
                description: 'Get the game client',
            },
            {
                label: 'Guide',
                to: 'https://litepaper.bravoready.com/br1-infinite-guide',
                access: 'all',
                external: true,
                description: 'How BR1 works, start to finish',
            },
        ],
    },
    {
        label: 'Earn',
        items: [
            {
                label: 'Tournaments',
                to: '/kill-race',
                access: 'auth',
                description: 'Compete in kill races for prizes',
            },
            {
                label: 'Inventory',
                to: '/renting',
                access: 'auth',
                description: 'Lock assets to accrue BR1 Points',
            },
            {
                label: 'Loot Boxes',
                to: '/loot-boxes',
                access: 'auth',
                description: 'Open boxes for in-game items',
            },
        ],
    },
    {
        // **Signed-in only.** Opening Marketplace to signed-out visitors was tried, to stop the bar
        // being a single PLAY item, and reversed: buying and trading are not what a first-time
        // visitor is here for, and a SHOP menu in front of someone with no account is noise. Signed
        // out the bar is PLAY plus the signup button, which is intended rather than an accident.
        //
        // Do not add a Collection entry here. It was added once and pulled straight back out; the
        // collection browser is not something the menu should advertise. The `/collection` route
        // still exists for anyone holding a link.
        label: 'Shop',
        items: [
            {
                label: 'Store',
                to: '/store',
                access: 'auth',
                description: 'Spend your balance and Points',
            },
            {
                // Staff always; players once the Packs kill switch in the
                // Django admin says "open to players". The real gate is the
                // server's; this only stops the menu advertising a 403.
                label: 'Packs',
                to: '/packs',
                access: 'packs',
                description: 'Open a pack for a random weapon',
            },
            {
                label: 'Marketplace',
                to: '/marketplace',
                access: 'auth',
                description: 'Trade assets with other players',
            },
            {
                // The BR1-native one, settled in BR1 balance. Staff always; players once the
                // marketplace kill switch says "open to players". Same shape as Packs.
                label: 'Trade',
                to: '/trade',
                access: 'marketplace',
                description: 'Buy and sell NFTs with your BR1 balance',
            },
        ],
    },
    {
        // A group of one renders as a plain link, so this stays a top-level Dashboard entry
        // without needing a second code path for ungrouped links.
        label: 'Dashboard',
        items: [{ label: 'Dashboard', to: '/dashboard', access: 'auth' }],
    },
]

/** Entries that live under the avatar rather than in the main bar. */
export const accountItems: NavItem[] = [
    { label: 'Account', to: '/account', access: 'auth' },
    // Beside Team Tools because it is the same kind of thing — a private working
    // surface you only see if you hold the role. It is not in the main bar: the
    // people who need it are a handful of approved creators, and the public
    // `/affiliate` pitch is what everyone else should be finding.
    { label: 'Affiliate Tools', to: '/affiliate/tools', access: 'affiliate' },
    // An agency sees both: the manager tools for its roster, and the affiliate
    // tools for any players it referred with its own link. Two surfaces because
    // they answer two different questions.
    { label: 'Manager Tools', to: '/affiliate/manager-tools', access: 'affiliate-manager' },
    { label: 'Team Tools', to: '/team-tools', access: 'staff' },
]

const isStaff = (auth: AuthUserType) =>
    [auth.is_staff, auth.is_developer, auth.is_marketing, auth.is_executive].some((role) => role === true)

/**
 * Mirrors the old wrappers exactly, including their behaviour while the viewer request is still
 * in flight: with no `auth` yet, everything gated stays hidden rather than flashing into view.
 */
export const canSee = (access: NavAccess, auth?: AuthUserType): boolean => {
    if (access === 'all') return true
    if (!auth) return false
    if (access === 'auth') return auth.is_auth === true
    // Deliberately not implied by any staff flag: staff are not affiliates, and
    // showing them a link to an earnings page they have no profile for would
    // land them on a 403.
    if (access === 'affiliate') return auth.is_auth === true && auth.is_affiliate === true
    if (access === 'affiliate-manager')
        return auth.is_auth === true && auth.is_affiliate_manager === true
    // Published to players by the kill switch, or staff regardless. Same
    // shape as the server's gate, minus the emergency stop, which the server
    // answers with a 403 the page then shows.
    if (access === 'packs') return auth.is_auth === true && (auth.packs_public === true || isStaff(auth))
    if (access === 'marketplace')
        return auth.is_auth === true && (auth.marketplace_public === true || isStaff(auth))
    // Team Tools also opens for somebody holding only a capability granted in
    // the admin -- an outside community manager with no role flag. Packs above
    // deliberately does not: a grant is one power, not staff.
    return auth.is_auth === true && (isStaff(auth) || auth.has_team_tools_grant === true)
}

/** The same question the `/packs` route asks: may this viewer use Packs. */
export const canUsePacks = (auth?: AuthUserType): boolean => canSee('packs', auth)

/** The same question the `/trade` route asks: may this viewer use the player marketplace. */
export const canUseMarketplace = (auth?: AuthUserType): boolean => canSee('marketplace', auth)

export const visibleItems = (items: NavItem[], auth?: AuthUserType): NavItem[] =>
    items.filter((item) => canSee(item.access, auth))
